import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  SupportAgent as SupportAgentIcon,
  CheckCircle as CheckCircleIcon,
  AutoAwesome as AutoAwesomeIcon,
  OpenInNew as OpenInNewIcon,
  AddPhotoAlternate as AddPhotoAlternateIcon,
  DeleteOutline as DeleteOutlineIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { getProductContext, recordContextEvent } from '../../utils/quoteFlowTelemetry';
import { buildSupportContext, formatSupportContext } from '../../utils/supportContext';

const INITIAL_FORM_STATE = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: ''
};

const MIN_MESSAGE_LENGTH = 10;
const MAX_NAME_LENGTH = 100;
const MAX_SUBJECT_LENGTH = 200;
const MAX_MESSAGE_LENGTH = 5000;
const SUBMIT_COOLDOWN_MS = 5000;
const MAX_ATTACHMENTS = 3;
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const ALLOWED_ATTACHMENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const OSTICKET_VIEW_URL = 'https://osticket.alw.dk/view.php';

/**
 * Sanitize input to remove potential HTML/script tags
 */
const sanitizeInput = (str) => str.trim().replace(/<[^>]*>/g, '');

/**
 * Get safe URL without query parameters or hash
 */
const getSafeUrl = () => `${window.location.origin}${window.location.pathname}`;

/**
 * Dialog til at sende support tickets til osTicket
 */
const SupportDialog = ({ open, onClose, context = {} }) => {
  const { enqueueSnackbar } = useSnackbar();
  const lastSubmitTime = useRef(0);

  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [queued, setQueued] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const [errors, setErrors] = useState({});
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMissingInformation, setAiMissingInformation] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const contextSnapshot = useMemo(() => buildSupportContext({
    explicitContext: context,
    storedContext: getProductContext()
  }), [context]);

  useEffect(() => {
    if (open) {
      recordContextEvent({ apiBaseUrl: API_BASE_URL, eventType: 'feedback_opened' });
    }
  }, [open]);

  const handleChange = useCallback((field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    // Use functional update to avoid dependency on errors
    setErrors((prev) => {
      if (prev[field]) {
        const { [field]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const handleAttachmentChange = useCallback(async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';

    const availableSlots = MAX_ATTACHMENTS - attachments.length;
    if (availableSlots <= 0) {
      enqueueSnackbar(`Du kan højst vedhæfte ${MAX_ATTACHMENTS} billeder`, { variant: 'warning' });
      return;
    }

    const acceptedFiles = selectedFiles.slice(0, availableSlots);
    if (selectedFiles.length > availableSlots) {
      enqueueSnackbar(`Kun de første ${availableSlots} billeder blev valgt`, { variant: 'warning' });
    }

    const invalidFile = acceptedFiles.find(file => (
      !ALLOWED_ATTACHMENT_TYPES.includes(file.type) || file.size > MAX_ATTACHMENT_BYTES
    ));
    if (invalidFile) {
      enqueueSnackbar('Billeder skal være JPG, PNG eller WebP og højst 5 MB pr. fil', { variant: 'error' });
      return;
    }

    try {
      const encodedFiles = await Promise.all(acceptedFiles.map(file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({
          name: file.name,
          type: file.type,
          size: file.size,
          data: reader.result
        });
        reader.onerror = () => reject(new Error(`Kunne ikke læse ${file.name}`));
        reader.readAsDataURL(file);
      })));
      setAttachments(prev => [...prev, ...encodedFiles]);
    } catch (error) {
      enqueueSnackbar(error.message || 'Kunne ikke læse billederne', { variant: 'error' });
    }
  }, [attachments.length, enqueueSnackbar]);

  const handleRemoveAttachment = useCallback((attachmentIndex) => {
    setAttachments(prev => prev.filter((_, index) => index !== attachmentIndex));
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Navn er påkrævet';
    } else if (formData.name.length > MAX_NAME_LENGTH) {
      newErrors.name = `Navn må maksimalt være ${MAX_NAME_LENGTH} tegn`;
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email er påkrævet';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ugyldig email adresse';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Emne er påkrævet';
    } else if (formData.subject.length > MAX_SUBJECT_LENGTH) {
      newErrors.subject = `Emne må maksimalt være ${MAX_SUBJECT_LENGTH} tegn`;
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Besked er påkrævet';
    } else if (formData.message.trim().length < MIN_MESSAGE_LENGTH) {
      newErrors.message = `Beskeden skal være mindst ${MIN_MESSAGE_LENGTH} tegn`;
    } else if (formData.message.length > MAX_MESSAGE_LENGTH) {
      newErrors.message = `Beskeden må maksimalt være ${MAX_MESSAGE_LENGTH} tegn`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    // Rate limiting - prevent rapid submissions
    const now = Date.now();
    if (now - lastSubmitTime.current < SUBMIT_COOLDOWN_MS) {
      enqueueSnackbar('Vent venligst et par sekunder før du sender igen', { variant: 'warning' });
      return;
    }

    if (!validateForm()) {
      enqueueSnackbar('Ret venligst fejlene i formularen', { variant: 'warning' });
      return;
    }

    lastSubmitTime.current = now;
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/support/tickets`, {
        name: sanitizeInput(formData.name),
        email: sanitizeInput(formData.email),
        phone: sanitizeInput(formData.phone),
        subject: sanitizeInput(formData.subject),
        message: `${sanitizeInput(formData.message)}\n\n---\n${formatSupportContext(contextSnapshot)}`,
        attachments: attachments.map(({ name, type, data }) => ({ name, type, data }))
      });

      if (response.data?.success) {
        const createdTicketId = response.data.ticketId
          ? String(response.data.ticketId).trim()
          : null;
        setTicketId(createdTicketId);
        setQueued(Boolean(response.data.queued));
        setSuccess(true);
        setFormData(INITIAL_FORM_STATE);
        setAttachments([]);
        recordContextEvent({ apiBaseUrl: API_BASE_URL, eventType: 'feedback_submitted' });
        enqueueSnackbar(
          response.data.queued
            ? 'Henvendelsen er gemt, og support er varslet.'
            : (createdTicketId ? `Support ticket ${createdTicketId} sendt!` : 'Support ticket sendt!'),
          { variant: response.data.queued ? 'warning' : 'success' }
        );
      } else {
        throw new Error(response.data?.error || 'Ukendt fejl');
      }
    } catch (error) {
      const rawError = error.response?.data?.error || error.message || 'Ukendt fejl';
      const errorMessage = typeof rawError === 'string' && /<!doctype|<html/i.test(rawError)
        ? 'Supportsystemet returnerede et ugyldigt svar. Prøv igen senere.'
        : rawError;
      enqueueSnackbar(`Kunne ikke sende ticket: ${errorMessage}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [validateForm, formData, enqueueSnackbar, contextSnapshot, attachments]);

  const handleCreateWithAi = useCallback(async () => {
    if (formData.subject.trim().length < 3 && formData.message.trim().length < 3) {
      enqueueSnackbar('Skriv et par ord om problemet først', { variant: 'info' });
      return;
    }
    setAiLoading(true);
    setAiMissingInformation([]);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/ai/support-draft`, {
        subject: formData.subject,
        message: formData.message,
        page: getSafeUrl()
      });
      let job = response.data.job;
      for (let attempt = 0; attempt < 90; attempt += 1) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const status = await axios.get(`${API_BASE_URL}/api/ai/jobs/${job.id}`);
        job = status.data.job;
        if (job.status === 'failed') throw new Error(job.error || 'AI-udkastet fejlede');
        if (job.status === 'completed') break;
      }
      if (job.status !== 'completed' || !job.result) throw new Error('AI-udkastet nåede ikke at blive færdigt');
      setFormData(prev => ({
        ...prev,
        subject: String(job.result.subject || prev.subject).slice(0, MAX_SUBJECT_LENGTH),
        message: String(job.result.message || prev.message).slice(0, MAX_MESSAGE_LENGTH)
      }));
      setAiMissingInformation(job.result.missingInformation || []);
      enqueueSnackbar('AI-udkast oprettet. Gennemlæs det før afsendelse.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || error.message || 'AI-udkastet fejlede', { variant: 'error' });
    } finally {
      setAiLoading(false);
    }
  }, [formData.subject, formData.message, enqueueSnackbar]);

  const handleClose = useCallback(() => {
    if (!loading) {
      setFormData(INITIAL_FORM_STATE);
      setErrors({});
      setSuccess(false);
      setQueued(false);
      setTicketId(null);
      setAiMissingInformation([]);
      setAttachments([]);
      onClose();
    }
  }, [loading, onClose]);

  const handleSendAnother = useCallback(() => {
    setSuccess(false);
    setQueued(false);
    setTicketId(null);
    setFormData(INITIAL_FORM_STATE);
    setErrors({});
    setAiMissingInformation([]);
    setAttachments([]);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="support-dialog-title"
    >
      <DialogTitle id="support-dialog-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <SupportAgentIcon color="primary" />
            <Typography variant="h6" component="span">
              Feedback og support
            </Typography>
          </Box>
          <Tooltip title={loading ? 'Afventer afsendelse...' : 'Luk'}>
            <span>
              <IconButton
                onClick={handleClose}
                disabled={loading}
                size="small"
                aria-label="Luk"
              >
                <CloseIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {success ? (
          <Box textAlign="center" py={4}>
            <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={2}>
              <CheckCircleIcon color="success" fontSize="large" />
              <Typography variant="h6" color={queued ? 'warning.main' : 'success.main'}>
                {queued ? 'Henvendelse gemt' : 'Ticket sendt!'}
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" paragraph>
              {queued
                ? 'Henvendelsen er gemt lokalt, og support er varslet. Den går derfor ikke tabt.'
                : 'Vi har modtaget din henvendelse og vender tilbage hurtigst muligt.'}
            </Typography>
            {ticketId && (
              <Alert severity={queued ? 'warning' : 'success'} sx={{ mt: 2, mb: 1 }}>
                <Box display="flex" alignItems="center" justifyContent="center" gap={1.5} flexWrap="wrap">
                  <span>{queued ? 'Din lokale reference er ' : 'Dit ticketnummer er '}<strong>{ticketId}</strong></span>
                  {!queued && (
                    <Button
                      component="a"
                      href={OSTICKET_VIEW_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      variant="outlined"
                      endIcon={<OpenInNewIcon />}
                    >
                      Åbn ticket
                    </Button>
                  )}
                </Box>
              </Alert>
            )}
            <Button
              variant="outlined"
              onClick={handleSendAnother}
              sx={{ mt: 2 }}
            >
              Send en ny ticket
            </Button>
          </Box>
        ) : (
          <form onSubmit={handleSubmit} id="support-form">
            <Typography variant="body2" color="text.secondary" paragraph>
              Fortæl kort hvad der er besværligt, mangler eller kan gøres hurtigere.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Navn"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleChange('name')}
                  error={Boolean(errors.name)}
                  helperText={errors.name}
                  disabled={loading}
                  autoFocus
                  inputProps={{
                    maxLength: MAX_NAME_LENGTH,
                    'aria-describedby': errors.name ? 'name-error' : undefined
                  }}
                  FormHelperTextProps={{
                    id: 'name-error'
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  value={formData.email}
                  onChange={handleChange('email')}
                  error={Boolean(errors.email)}
                  helperText={errors.email}
                  disabled={loading}
                  inputProps={{
                    'aria-describedby': errors.email ? 'email-error' : undefined
                  }}
                  FormHelperTextProps={{
                    id: 'email-error'
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Telefon (valgfri)"
                  type="tel"
                  fullWidth
                  value={formData.phone}
                  onChange={handleChange('phone')}
                  disabled={loading}
                  placeholder="Fx. 12 34 56 78"
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: '1px dashed',
                    borderColor: attachments.length >= MAX_ATTACHMENTS ? 'action.disabled' : 'primary.main',
                    borderRadius: 2,
                    p: 1.5,
                    bgcolor: 'action.hover'
                  }}
                >
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<AddPhotoAlternateIcon />}
                    disabled={loading || attachments.length >= MAX_ATTACHMENTS}
                  >
                    Tilføj billeder
                    <input
                      hidden
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      onChange={handleAttachmentChange}
                    />
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    Foto eller galleri · JPG, PNG eller WebP · maks. 3 billeder á 5 MB
                  </Typography>
                  {attachments.map((attachment, index) => (
                    <Box
                      key={`${attachment.name}-${attachment.size}-${index}`}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                      mt={1}
                    >
                      <Typography variant="body2" noWrap title={attachment.name}>
                        {attachment.name} · {(attachment.size / (1024 * 1024)).toFixed(1)} MB
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveAttachment(index)}
                        disabled={loading}
                        aria-label={`Fjern ${attachment.name}`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Emne"
                  fullWidth
                  required
                  value={formData.subject}
                  onChange={handleChange('subject')}
                  error={Boolean(errors.subject)}
                  helperText={errors.subject}
                  disabled={loading}
                  placeholder="Kort beskrivelse af dit problem"
                  inputProps={{
                    maxLength: MAX_SUBJECT_LENGTH,
                    'aria-describedby': errors.subject ? 'subject-error' : undefined
                  }}
                  FormHelperTextProps={{
                    id: 'subject-error'
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Besked"
                  fullWidth
                  required
                  multiline
                  rows={5}
                  value={formData.message}
                  onChange={handleChange('message')}
                  error={Boolean(errors.message)}
                  helperText={errors.message || 'Beskriv dit problem så detaljeret som muligt'}
                  disabled={loading}
                  placeholder="Beskriv hvad du har brug for hjælp til..."
                  inputProps={{
                    maxLength: MAX_MESSAGE_LENGTH,
                    'aria-describedby': errors.message ? 'message-error' : undefined
                  }}
                  FormHelperTextProps={{
                    id: 'message-error'
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={aiLoading ? <CircularProgress size={18} /> : <AutoAwesomeIcon />}
                    onClick={handleCreateWithAi}
                    disabled={loading || aiLoading}
                  >
                    {aiLoading ? 'Opretter udkast...' : 'Opret med AI'}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    AI udfylder kun et redigerbart udkast og sender ikke ticketen.
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                Kontekst vedhæftes automatisk
              </Typography>
              <Typography variant="caption" component="div">
                {[contextSnapshot.viewLabel || contextSnapshot.viewId, contextSnapshot.projectId
                  ? `Projekt #${contextSnapshot.projectId}`
                  : null, contextSnapshot.stepId, contextSnapshot.viewport]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
              <Typography variant="caption" component="div" color="text.secondary">
                Ingen kundeoplysninger eller tilbudstekst indgår i målingen.
              </Typography>
            </Alert>
            {aiMissingInformation.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Tilføj gerne: {aiMissingInformation.join(', ')}
              </Alert>
            )}
          </form>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
          >
            Annuller
          </Button>
          <Button
            type="submit"
            form="support-form"
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {loading ? 'Sender...' : 'Send ticket'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

SupportDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  context: PropTypes.shape({
    viewId: PropTypes.string,
    viewLabel: PropTypes.string,
    projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stepId: PropTypes.string
  })
};

export default SupportDialog;
